/**
 * RefillsDropdown Component
 * A dropdown button that shows refill information and administration site fields
 * Designed to be placed in the card header area
 * Uses fixed positioning to escape overflow:hidden containers
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ADMINISTRATION_SITES, type AdministrationSite } from "@/services/travelers";

interface RefillsDropdownProps {
  medicationId: string;
  refillInfo: string;
  administrationSite: AdministrationSite | null;
  quantity?: string;
  onRefillInfoChange: (medicationId: string, refillInfo: string) => void;
  onAdministrationSiteChange: (medicationId: string, site: AdministrationSite | null) => void;
  onQuantityChange?: (medicationId: string, quantity: string) => void;
  onSaveDetails?: (
    medicationId: string,
    note: string,
    refillInfo: string,
    administrationSite: AdministrationSite | null,
    quantity?: string
  ) => Promise<boolean>;
  note: string;
  isSaving?: boolean;
  isVaccine?: boolean;
  isSelected?: boolean;
  /** Opens the record-administration modal for this vaccine (vaccines only) */
  onRecordAdministration?: (medicationId: string) => void;
}

interface DropdownPosition {
  top: number;
  left: number;
}

export function RefillsDropdown({
  medicationId,
  refillInfo,
  administrationSite,
  quantity = "",
  onRefillInfoChange,
  onAdministrationSiteChange,
  onQuantityChange,
  onSaveDetails,
  note,
  isSaving = false,
  isVaccine = true,
  isSelected = false,
  onRecordAdministration,
}: RefillsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<DropdownPosition>({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Calculate dropdown position based on button location
  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 300;
      const dropdownHeight = 280;
      const padding = 8;

      // Calculate initial position (below button, aligned to right edge)
      let top = rect.bottom + 4;
      let left = rect.right - dropdownWidth;

      // Adjust if dropdown would go off the left edge
      if (left < padding) {
        left = padding;
      }

      // Adjust if dropdown would go off the right edge
      if (left + dropdownWidth > window.innerWidth - padding) {
        left = window.innerWidth - dropdownWidth - padding;
      }

      // Adjust if dropdown would go off the bottom edge - show above instead
      if (top + dropdownHeight > window.innerHeight - padding) {
        top = rect.top - dropdownHeight - 4;
      }

      // Ensure top doesn't go negative
      if (top < padding) {
        top = padding;
      }

      setPosition({ top, left });
    }
  }, []);

  // Update position when opening and on scroll/resize
  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const handleSave = async () => {
    if (onSaveDetails) {
      const success = await onSaveDetails(medicationId, note, refillInfo, administrationSite, quantity);
      if (success) {
        setIsOpen(false);
      }
    }
  };

  // Check if there's data filled in
  const hasData = administrationSite || refillInfo;

  // Determine button state based on selection
  const isDisabled = !isSelected;

  // Dropdown panel content - rendered via portal to escape overflow:hidden
  const dropdownPanel = isOpen && (
    <div
      ref={dropdownRef}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        width: "min(300px, calc(100vw - 32px))",
        backgroundColor: "#ffffff",
        borderRadius: "10px",
        boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.05)",
        border: "1px solid #e5e7eb",
        zIndex: 99999,
        padding: "16px",
        maxHeight: "400px",
        overflowY: "auto",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "14px",
          paddingBottom: "10px",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <div
          style={{
            fontSize: "13px",
            fontWeight: 600,
            color: "#1f2937",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#14b8a6"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 3v18h18" />
            <path d="m19 9-5 5-4-4-3 3" />
          </svg>
          Refills & Administration
        </div>
        <button
          onClick={() => setIsOpen(false)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#9ca3af",
            borderRadius: "4px",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f3f4f6";
            e.currentTarget.style.color = "#6b7280";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "#9ca3af";
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {/* Administration Site Buttons - only for vaccines */}
        {isVaccine && (
          <div>
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: 600,
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Administration Site
            </label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "6px",
              }}
            >
              {ADMINISTRATION_SITES.map((site) => {
                const isActive = administrationSite === site;
                const isLeft = site.startsWith("Left");
                const isSub = site.includes("Subcutaneous");
                const shortLabel = `${isLeft ? "L" : "R"} ${isSub ? "Subcut" : "Deltoid IM"}`;

                return (
                  <button
                    key={site}
                    type="button"
                    onClick={() =>
                      onAdministrationSiteChange(
                        medicationId,
                        isActive ? null : site
                      )
                    }
                    style={{
                      padding: "8px 6px",
                      fontSize: "11px",
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? "#ffffff" : "#374151",
                      backgroundColor: isActive ? "#14b8a6" : "#f9fafb",
                      border: `1.5px solid ${isActive ? "#0d9488" : "#d1d5db"}`,
                      borderRadius: "6px",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      textAlign: "center",
                      lineHeight: "1.3",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = "#f0fdfa";
                        e.currentTarget.style.borderColor = "#14b8a6";
                        e.currentTarget.style.color = "#0f766e";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = "#f9fafb";
                        e.currentTarget.style.borderColor = "#d1d5db";
                        e.currentTarget.style.color = "#374151";
                      }
                    }}
                    title={site}
                  >
                    {shortLabel}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Quantity */}
        {!isVaccine && onQuantityChange && (
          <div>
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: 600,
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Quantity
            </label>
            <input
              type="text"
              inputMode="numeric"
              placeholder="e.g., 30"
              value={quantity}
              onChange={(e) => {
                const val = e.target.value;
                // Allow empty string (clearing) or digits only
                if (val === '' || /^\d+$/.test(val)) {
                  onQuantityChange(medicationId, val);
                }
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: "13px",
                color: "#1f2937",
                backgroundColor: "#ffffff",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontFamily: "inherit",
                outline: "none",
                transition: "border-color 0.2s, box-shadow 0.2s",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#14b8a6";
                e.currentTarget.style.boxShadow = "0 0 0 3px rgba(20, 184, 166, 0.1)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "#d1d5db";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
          </div>
        )}

        {/* Refill Information */}
        <div>
          <label
            style={{
              display: "block",
              fontSize: "12px",
              fontWeight: 600,
              color: "#374151",
              marginBottom: "6px",
            }}
          >
            Refill Information
          </label>
          <input
            type="text"
            inputMode="numeric"
            placeholder="e.g., 2"
            value={refillInfo}
            onChange={(e) => {
              const val = e.target.value;
              // Allow empty string (clearing) or digits only
              if (val === '' || /^\d+$/.test(val)) {
                onRefillInfoChange(medicationId, val);
              }
            }}
            style={{
              width: "100%",
              padding: "10px 12px",
              fontSize: "13px",
              color: "#1f2937",
              backgroundColor: "#ffffff",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontFamily: "inherit",
              outline: "none",
              transition: "border-color 0.2s, box-shadow 0.2s",
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = "#14b8a6";
              e.currentTarget.style.boxShadow = "0 0 0 3px rgba(20, 184, 166, 0.1)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "#d1d5db";
              e.currentTarget.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Save Button */}
        {onSaveDetails && (
          <button
            onClick={handleSave}
            disabled={isSaving}
            style={{
              width: "100%",
              padding: "10px 16px",
              fontSize: "13px",
              fontWeight: 600,
              color: isSaving ? "#9ca3af" : "#ffffff",
              backgroundColor: isSaving ? "#e5e7eb" : "#14b8a6",
              border: "none",
              borderRadius: "8px",
              cursor: isSaving ? "not-allowed" : "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (!isSaving) {
                e.currentTarget.style.backgroundColor = "#0d9488";
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving) {
                e.currentTarget.style.backgroundColor = "#14b8a6";
              }
            }}
          >
            {isSaving ? "Saving..." : "Save Details"}
          </button>
        )}

        {/* Record Administration - only for vaccines */}
        {isVaccine && onRecordAdministration && (
          <button
            onClick={() => {
              setIsOpen(false);
              onRecordAdministration(medicationId);
            }}
            style={{
              width: "100%",
              padding: "10px 16px",
              fontSize: "13px",
              fontWeight: 600,
              color: "#0f766e",
              backgroundColor: "#f0fdfa",
              border: "1.5px solid #14b8a6",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#ccfbf1";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#f0fdfa";
            }}
          >
            Record Administration
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          if (!isDisabled) {
            setIsOpen(!isOpen);
          }
        }}
        disabled={isDisabled}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          padding: "4px 8px",
          border: "1px solid",
          borderColor: isDisabled ? "#e5e7eb" : hasData ? "#14b8a6" : "#d1d5db",
          borderRadius: "4px",
          fontSize: "10px",
          fontWeight: 600,
          cursor: isDisabled ? "not-allowed" : "pointer",
          backgroundColor: isDisabled ? "#f9fafb" : hasData ? "#f0fdfa" : "white",
          color: isDisabled ? "#9ca3af" : hasData ? "#0f766e" : "#6b7280",
          opacity: isDisabled ? 0.6 : 1,
          transition: "all 0.2s",
          whiteSpace: "nowrap",
        }}
        title={isDisabled ? "Select this item first to add details" : "Refills & Administration"}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {isVaccine ? (
            // Syringe icon for vaccines
            <>
              <path d="m18 2 4 4" />
              <path d="m17 7 3-3" />
              <path d="M19 9 8.7 19.3c-1 1-2.5 1-3.4 0l-.6-.6c-1-1-1-2.5 0-3.4L15 5" />
              <path d="m9 11 4 4" />
              <path d="m5 19-3 3" />
              <path d="m14 4 6 6" />
            </>
          ) : (
            // Pill icon for medications
            <>
              <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
              <path d="m8.5 8.5 7 7" />
            </>
          )}
        </svg>
        <span style={{ display: "flex", alignItems: "center", gap: "2px" }}>
          Details
          {hasData && (
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="currentColor"
              style={{ marginLeft: "2px" }}
            >
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
          )}
        </span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {/* Render dropdown via portal to escape overflow:hidden */}
      {typeof document !== "undefined" && createPortal(dropdownPanel, document.body)}
    </div>
  );
}

export default RefillsDropdown;
